import type { PushNotificationRefs } from './push-navigation';

/**
 * Message types exchanged between the service worker and main thread via postMessage.
 * Both contexts import from this file — keep it free of DOM/SW-specific APIs.
 */
export const SW_MESSAGE = {
  /** User accepted the "update available" prompt → SW calls skipWaiting() */
  SKIP_WAITING: 'SKIP_WAITING',
  /** SW detected a 404 for a cached asset → clients should refresh */
  STALE_ASSETS_DETECTED: 'STALE_ASSETS_DETECTED',
  /** User clicked a notification → navigate the focused tab to the payload URL */
  NOTIFICATION_NAVIGATE: 'NOTIFICATION_NAVIGATE',
  /** Push subscription invalidated (e.g., expired or revoked by push service) → re-query subscription */
  PUSH_SUBSCRIPTION_CHANGED: 'PUSH_SUBSCRIPTION_CHANGED',
  /** Chat push received while user has the matching chat screen focused → suppress notification and invalidate caches */
  CHAT_MESSAGE_RECEIVED: 'CHAT_MESSAGE_RECEIVED',
} as const;

export type SwMessageType = (typeof SW_MESSAGE)[keyof typeof SW_MESSAGE];

/**
 * Payload for NOTIFICATION_NAVIGATE (N1 destination contract, task W3).
 *
 * Dual-read transition:
 * - `refs` carries the flattened notification context scalars ({ type,
 *   ...string entries of the push payload's data }) when the payload declared
 *   a type. The PAGE resolves them via resolveNotificationTarget; the service
 *   worker never resolves destinations itself.
 * - `url` stays populated with the legacy safe path so pages older than the
 *   SW (and payloads with no type) keep navigating exactly as before, through
 *   the isSafeInAppPath gate.
 */
export interface NotificationNavigateMessage {
  type: typeof SW_MESSAGE.NOTIFICATION_NAVIGATE;
  url?: string;
  refs?: PushNotificationRefs;
}
